import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  createEmptyEvent,
  normalizeEvents,
  seededPublicEvents,
} from '../data/events';
import { useSiteAuth } from '../hooks/useSiteAuth';
import { createBlissEvent, deleteBlissEvent, listBlissEvents, replaceBlissEvents, updateBlissEvent } from '../services/events-api';
import type { PublicEvent } from '../types';
import { EventManagerContext } from './eventManagerContext';

const LOCAL_EVENTS_KEY = 'victorias-place-local-events';

function readLocalEvents(): PublicEvent[] {
  if (typeof window === 'undefined') {
    return normalizeEvents(seededPublicEvents);
  }

  try {
    const raw = window.localStorage.getItem(LOCAL_EVENTS_KEY);
    if (!raw) {
      return normalizeEvents(seededPublicEvents);
    }

    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? normalizeEvents(parsed as PublicEvent[]) : normalizeEvents(seededPublicEvents);
  } catch {
    return normalizeEvents(seededPublicEvents);
  }
}

function writeLocalEvents(events: PublicEvent[]) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(LOCAL_EVENTS_KEY, JSON.stringify(normalizeEvents(events)));
}

export function EventManagerProvider({ children }: { children: ReactNode }) {
  const { session, runtime, runtimeError, isInitializing } = useSiteAuth();
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [storageMode, setStorageMode] = useState<'bliss' | 'local'>('local');

  useEffect(() => {
    let isActive = true;

    async function loadEvents() {
      if (isInitializing) {
        return;
      }

      if (!runtime) {
        if (isActive) {
          setEvents(readLocalEvents());
          setStorageMode('local');
          setSyncError(runtimeError);
          setIsLoading(false);
        }
        return;
      }

      if (isActive) {
        setIsLoading(true);
      }

      try {
        const nextEvents = await listBlissEvents(session?.role === 'admin');
        if (isActive) {
          setEvents(normalizeEvents(nextEvents));
          setStorageMode('bliss');
          setSyncError(null);
        }
      } catch (error) {
        if (isActive) {
          setEvents(readLocalEvents());
          setStorageMode('local');
          setSyncError(
            error instanceof Error ? error.message : 'The events workspace fell back to local seed data.',
          );
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }

    void loadEvents();

    return () => {
      isActive = false;
    };
  }, [isInitializing, runtime, runtimeError, session?.role]);

  const publishedEvents = useMemo(
    () => events.filter((event) => event.isPublished),
    [events],
  );

  const featuredEvent = useMemo(
    () => publishedEvents.find((event) => event.featured) ?? publishedEvents[0] ?? null,
    [publishedEvents],
  );

  function commit(nextEvents: PublicEvent[]) {
    const normalizedEvents = normalizeEvents(nextEvents);
    setEvents(normalizedEvents);
    if (storageMode === 'local') {
      writeLocalEvents(normalizedEvents);
    }
  }

  function createEvent() {
    return createEmptyEvent();
  }

  async function saveEvent(nextEvent: PublicEvent) {
    const normalizedEvent = normalizeEvents([nextEvent])[0];

    if (storageMode === 'bliss' && runtime) {
      const exists = events.some((event) => event.id === normalizedEvent.id);
      const savedEvent = exists ? await updateBlissEvent(normalizedEvent) : await createBlissEvent(normalizedEvent);
      const reloadedEvents = await listBlissEvents(session?.role === 'admin');
      commit(reloadedEvents);
      setSyncError(null);
      return reloadedEvents.find((event) => event.id === savedEvent.id) ?? savedEvent;
    }

    commit(
      events.some((event) => event.id === normalizedEvent.id)
        ? events.map((event) => (event.id === normalizedEvent.id ? normalizedEvent : event))
        : [normalizedEvent, ...events],
    );
    return normalizedEvent;
  }

  async function deleteEvent(eventId: string) {
    if (storageMode === 'bliss' && runtime) {
      await deleteBlissEvent(eventId);
      const reloadedEvents = await listBlissEvents(session?.role === 'admin');
      commit(reloadedEvents);
      setSyncError(null);
      return;
    }

    commit(events.filter((event) => event.id !== eventId));
  }

  async function setFeaturedEvent(eventId: string) {
    const selectedEvent = events.find((event) => event.id === eventId);
    if (!selectedEvent) {
      return;
    }

    const nextEvent = {
      ...selectedEvent,
      isPublished: true,
      featured: true,
    };

    if (storageMode === 'bliss' && runtime) {
      await updateBlissEvent(nextEvent);
      const reloadedEvents = await listBlissEvents(session?.role === 'admin');
      commit(reloadedEvents);
      setSyncError(null);
      return;
    }

    commit(
      events.map((event) => ({
        ...event,
        featured: event.id === eventId && event.isPublished,
      })),
    );
  }

  async function resetEvents() {
    if (storageMode === 'bliss' && runtime) {
      const replaced = await replaceBlissEvents(seededPublicEvents);
      commit(replaced);
      setSyncError(null);
      return;
    }

    commit(seededPublicEvents);
  }

  function getEventBySlug(slug: string) {
    return publishedEvents.find((event) => event.slug === slug);
  }

  return (
    <EventManagerContext.Provider
      value={{
        isLoading,
        syncError,
        storageMode,
        events,
        publishedEvents,
        featuredEvent,
        createEvent,
        saveEvent,
        deleteEvent,
        setFeaturedEvent,
        resetEvents,
        getEventBySlug,
      }}
    >
      {children}
    </EventManagerContext.Provider>
  );
}
